import React from 'react';
import { bearerHeaders } from '../api.ts';
import { resolveUrl } from '../api';
import { getTheme, useThemeMode } from '../theme';
import { STATUS_CONFIG } from './constants';
import { mediaPublicUrl, viltPublicUrl } from './pinUtils';
import { useQrDataUrl } from './useQrDataUrl';
import type { FalltViltEvent, MarkPin } from './types';

export function ViltDetailsPanel({ pin, token, onClose, onStatusUpdate, onPinUpdate }: {
  pin: MarkPin; token: string;
  onClose: () => void;
  onStatusUpdate: (pinId: string, status: string) => void;
  onPinUpdate: (pin: MarkPin) => void;
}) {
  const [comment, setComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [events, setEvents] = React.useState<FalltViltEvent[]>(pin.falltViltEvents || []);
  const [showQr, setShowQr] = React.useState(false);

  const viltUrl = viltPublicUrl(pin.id);
  const qrSrc = useQrDataUrl(showQr ? viltUrl : '');

  const gqlFetch2 = async (query: string, variables?: any) => {
    const res = await fetch(resolveUrl('/graphql'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': 'dev-secret', ...bearerHeaders(token) },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0].message);
    return json.data;
  };

  const handleStatus = async (status: string) => {
    setSubmitting(true);
    try {
      const data = await gqlFetch2(
        `mutation($pinId: ID!, $status: String!) { updateFalltViltStatus(pinId: $pinId, status: $status) { id eventType oldStatus newStatus commentText createdAt } }`,
        { pinId: pin.id, status }
      );
      setEvents(prev => [...prev, data.updateFalltViltStatus]);
      onStatusUpdate(pin.id, status);
    } catch (e: any) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const data = await gqlFetch2(
        `mutation($pinId: ID!, $comment: String!) { addFalltViltComment(pinId: $pinId, comment: $comment) { id eventType commentText createdAt authorEmail } }`,
        { pinId: pin.id, comment: comment.trim() }
      );
      setEvents(prev => [...prev, data.addFalltViltComment]);
      setComment('');
    } catch (e: any) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  const currentStatus = pin.falltStatus || 'fallt';
  const statusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.fallt;

  const { mode } = useThemeMode();
  const th = React.useMemo(() => getTheme(mode), [mode]);
  const T2 = React.useMemo(() => ({
    card: th.surface,
    inset: th.bg,
    border: th.border,
    text: th.text,
    sub: th.textSub,
    muted: th.textMuted,
    amber: th.amber,
    overlay: th.overlay,
  }), [th]);

  const fmtTime = (iso: string) => {
    try { return new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
  };

  const eventLabel = (ev: FalltViltEvent) => {
    if (ev.eventType === 'status_change') {
      const ns = STATUS_CONFIG[ev.newStatus || '']?.label || ev.newStatus || '';
      return `Status: ${ns}${ev.commentText ? ' — ' + ev.commentText : ''}`;
    }
    if (ev.eventType === 'comment') return ev.commentText || '';
    if (ev.eventType === 'move') return ev.commentText || 'Flytt';
    return ev.commentText || ev.eventType;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: T2.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: T2.card, borderRadius: 8, padding: 0, width: '90%', maxWidth: 480, maxHeight: '88vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', border: `1px solid ${T2.border}`, boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${T2.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ background: `${statusCfg.color}22`, color: statusCfg.color, fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10, border: `1px solid ${statusCfg.color}44` }}>{statusCfg.label}</span>
            </div>
            <div style={{ color: T2.text, fontWeight: 700, fontSize: 18 }}>{pin.name || pin.viltslag || 'Fällt vilt'}</div>
            {pin.viltslag && <div style={{ color: T2.sub, fontSize: 13, marginTop: 2 }}>{pin.viltslag}{pin.kon && pin.kon !== 'Okänt' ? ` · ${pin.kon}` : ''}{pin.alder && pin.alder !== 'Okänt' ? ` · ${pin.alder}` : ''}</div>}
            {pin.jagarEmail && <div style={{ color: T2.muted, fontSize: 12, marginTop: 2 }}>Jägare: {pin.jagarEmail}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T2.muted, fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 20px' }}>
          {/* Photos */}
          {pin.photoUrls && pin.photoUrls.length > 0 && (
            <div style={{ marginBottom: 14, display: 'flex', gap: 8, overflowX: 'auto' }}>
              {pin.photoUrls.map((url, i) => (
                <img key={i} src={mediaPublicUrl(url)} alt="Foto" style={{ height: 100, width: 'auto', borderRadius: 4, border: `1px solid ${T2.border}`, flexShrink: 0 }} />
              ))}
            </div>
          )}

          {/* Status actions */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: T2.sub, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Uppdatera status</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_CONFIG).filter(([k]) => k !== currentStatus).map(([k, v]) => (
                <button key={k} onClick={() => handleStatus(k)} disabled={submitting} style={{
                  padding: '6px 14px', borderRadius: 4, border: `1px solid ${v.color}`,
                  background: 'transparent', color: v.color, fontWeight: 600, cursor: 'pointer', fontSize: 12,
                  opacity: submitting ? 0.6 : 1,
                }}>{v.label}</button>
              ))}
            </div>
          </div>

          {/* QR Code */}
          <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShowQr(!showQr)} style={{
              background: 'transparent', border: `1px solid ${T2.border}`, borderRadius: 4,
              color: T2.sub, fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer', width: '100%', textAlign: 'left',
            }}>
              {showQr ? '▲ Dölj QR-kod' : '▼ Visa QR-kod (för märkning)'}
            </button>
            {showQr && (
              <div style={{ marginTop: 10, textAlign: 'center', background: T2.inset, borderRadius: 5, padding: 14, border: `1px solid ${T2.border}` }}>
                <img src={qrSrc} alt="QR-kod" style={{ width: 160, height: 160, borderRadius: 4 }} />
                <div style={{ color: T2.muted, fontSize: 11, marginTop: 6 }}>{viltUrl}</div>
                {pin.qregistryObjectId && <div style={{ color: T2.sub, fontSize: 11, marginTop: 4 }}>Qregistry: {pin.qregistryObjectId}</div>}
              </div>
            )}
          </div>

          {/* Events timeline */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: T2.sub, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Händelselogg</div>
            {events.length === 0 ? (
              <div style={{ color: T2.muted, fontSize: 13 }}>Inga händelser</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {events.map(ev => (
                  <div key={ev.id} style={{ background: T2.inset, borderRadius: 4, padding: '8px 12px', border: `1px solid ${T2.border}` }}>
                    <div style={{ color: T2.text, fontSize: 13 }}>{eventLabel(ev)}</div>
                    <div style={{ color: T2.muted, fontSize: 11, marginTop: 2 }}>{fmtTime(ev.createdAt)}{ev.authorEmail ? ` · ${ev.authorEmail}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add comment */}
          <div>
            <div style={{ color: T2.sub, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Lägg till kommentar</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Kommentar..."
                style={{ flex: 1, background: T2.inset, border: `1px solid ${T2.border}`, borderRadius: 4, padding: '8px 12px', color: T2.text, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                onKeyDown={e => e.key === 'Enter' && handleComment()}
              />
              <button onClick={handleComment} disabled={submitting || !comment.trim()} style={{
                padding: '8px 14px', borderRadius: 4, border: 'none', background: T2.amber,
                color: '#0a0a0a', fontWeight: 700, cursor: 'pointer', fontSize: 13,
                opacity: submitting || !comment.trim() ? 0.5 : 1,
              }}>OK</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
